/**
 * Edit Student Page
 * Edit student information
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { apiRequest } from '@/api/client';

interface Student {
  studentId: string;
  firstName: string;
  lastName: string;
  username: string;
  classes: string[];
}

interface ClassOption {
  classId: string;
  className: string;
  gradeLevel: string;
}

export const EditStudentPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  const [student, setStudent] = useState<Student | null>(null);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    classId: '',
  });

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch student details
      const studentResponse = await apiRequest<{ student: Student }>('get_student_by_id', {
        studentId,
        teacherEmail: user?.email,
      });
      
      if (studentResponse.success && (studentResponse as any).student) {
        const studentData = (studentResponse as any).student;
        setStudent(studentData);
        setFormData({
          firstName: studentData.firstName,
          lastName: studentData.lastName,
          classId: studentData.classes?.[0] || '',
        });
      }

      // Fetch classes
      const classesResponse = await apiRequest<{ classes: ClassOption[] }>('get_school_classes', {
        schoolId: user?.schoolId,
      });
      
      if (classesResponse.success && (classesResponse as any).classes) {
        setClasses((classesResponse as any).classes);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showNotification('error', 'Failed to load student data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      showNotification('error', 'Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      
      const response = await apiRequest('update_student', {
        studentId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        currentClassId: formData.classId,
        teacherEmail: user?.email,
        schoolId: user?.schoolId,
      });

      if (response.success) {
        showNotification('success', 'Student updated successfully');
        navigate(`/students/${studentId}`);
      } else {
        showNotification('error', (response as any).message || 'Failed to update student');
      }
    } catch (error: any) {
      console.error('Failed to update student:', error);
      
      // Check if it's a 400 error indicating missing endpoint
      if (error.message?.includes('400')) {
        showNotification('error', 'Update student endpoint not yet implemented in backend');
      } else {
        showNotification('error', 'Failed to update student');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Student not found
            </h3>
            <p className="text-slate-600 mb-6">
              This student may have been deleted or you don't have permission to view them.
            </p>
            <Button variant="primary" onClick={() => navigate('/students')}>
              Back to Students
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Edit Student</h1>
          <p className="text-slate-600 mt-2">Update student information</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/students/${studentId}`)}>
          ← Cancel
        </Button>
      </div>

      {/* Warning about backend */}
      <Card>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Changes to student information will be saved to the database.
          </p>
        </div>
      </Card>

      {/* Edit Form */}
      <Card title="Student Information">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Username (Read-only)
            </label>
            <input
              type="text"
              value={student.username}
              disabled
              className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-100 text-slate-600 cursor-not-allowed"
            />
            <p className="text-sm text-slate-500 mt-1">
              Username cannot be changed after creation
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Class
            </label>
            <select
              value={formData.classId}
              onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Select a class</option>
              {classes.map((cls) => (
                <option key={cls.classId} value={cls.classId}>
                  {cls.className} ({cls.gradeLevel})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/students/${studentId}`)}
              disabled={isSaving}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
